import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Box,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

// Hard cap on the produced avatar so the backend (500 KB limit) never
// rejects a freshly cropped photo. A 4000×4000 source previously yielded
// ~2 MB at quality 0.85; 512×512 lands around 40–80 KB which is plenty
// sharp for the 120 px avatar in the UI.
const MAX_AVATAR_DIM = 512;

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Scale the crop down to fit within MAX_AVATAR_DIM while preserving the
  // 1:1 aspect ratio enforced by the cropper.
  const longestSide = Math.max(pixelCrop.width, pixelCrop.height);
  const scale = longestSide > MAX_AVATAR_DIM ? MAX_AVATAR_DIM / longestSide : 1;
  const targetWidth = Math.round(pixelCrop.width * scale);
  const targetHeight = Math.round(pixelCrop.height * scale);

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  return canvas.toDataURL('image/jpeg', 0.85);
}

const CropModal = ({ open, onClose, imageSrc, onCropComplete }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropCompleteInternal = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (croppedAreaPixels && imageSrc) {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImage);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('profile.cropTitle', 'Crop Photo')}</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', height: 400, bgcolor: '#333' }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={0.5}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onCropComplete={onCropCompleteInternal}
              onZoomChange={setZoom}
            />
          )}
        </Box>
        <Box sx={{ mt: 2, px: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={0.5}
            max={3}
            step={0.1}
            onChange={(e, zoom) => setZoom(zoom)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('profile.cropCancel', 'Cancel')}
        </Button>
        <Button onClick={handleApply} variant="contained">
          {t('profile.cropConfirm', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CropModal;
